import type { ReactNode, RefObject } from 'react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './HourlyDataTable.module.less'

type HourlyDataTableLeadingColumn = {
  /** Content of the header cell, which spans the sub-header row too */
  header: ReactNode
  renderCell: (rowIndex: number) => ReactNode
}

type HourlyDataTableProps = {
  rowCount: number
  /** <th> elements for the primary (top) header row */
  columnHeaders: ReactNode
  /** <th> elements for the optional sub-header row (e.g. Buy / Sell labels) */
  subColumnHeaders?: ReactNode
  /** Returns the data <td> cells for a given row index */
  renderCellsForRow: (rowIndex: number) => ReactNode
  /** Text shown in the row's sticky left label cell (e.g. "08-09") */
  getRowLabel: (rowIndex: number) => string
  /** Optional class for a row with special meaning, such as a summary row. */
  getRowClassName?: (rowIndex: number) => string
  /** If provided, date-separator rows are rendered between calendar days */
  getDateLabel?: (rowIndex: number) => string
  /** Ref attached to the scroll container — used by clipboard and selection hooks */
  tableContainerRef: RefObject<HTMLDivElement | null>
  containerClassName?: string
  /** Applies compact sizing for tables with auto-width columns. */
  isCompact?: boolean
  /** Grows the table to its full content height instead of capping it, so the surrounding view is
   *  the only vertical scroll. Horizontal scroll stays inside the table, so the column headers
   *  scroll away with the page instead of sticking. */
  isFullHeight?: boolean
  /** Total number of columns including the sticky row label column and the leading column.
   *  Used for date-separator row colSpan. Defaults to a large value if not provided. */
  totalColumnCount?: number
  /** A sticky column to the left of the row label column, for a control that acts on the row. */
  leadingColumn?: HourlyDataTableLeadingColumn
}

export function HourlyDataTable({
  rowCount,
  columnHeaders,
  subColumnHeaders,
  renderCellsForRow,
  getRowLabel,
  getRowClassName,
  getDateLabel,
  tableContainerRef,
  containerClassName,
  isCompact = false,
  isFullHeight = false,
  totalColumnCount = 999,
  leadingColumn,
}: HourlyDataTableProps): ReactNode {
  const { t } = useTranslation()
  const rowIndices = Array.from({ length: rowCount }, (_, i) => i)
  const hourSpan = subColumnHeaders ? 2 : 1

  return (
    <div
      className={[
        styles.HourlyDataTable_BidTableWrapper,
        isCompact ? styles.HourlyDataTable_Compact : '',
        isFullHeight ? styles.HourlyDataTable_FullHeight : '',
        leadingColumn ? styles.HourlyDataTable_WithLeadingColumn : '',
        containerClassName ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      ref={tableContainerRef}
    >
      <table className={styles.HourlyDataTable_BidTable}>
        <thead>
          <tr>
            {leadingColumn && (
              <th
                className={[
                  styles.HourlyDataTable_BidTableTh,
                  styles.HourlyDataTable_BidTableHourBoundaryHeader,
                  styles.HourlyDataTable_BidTableStickyTopPrimary,
                  styles.HourlyDataTable_BidTableLeadingHeader,
                ].join(' ')}
                rowSpan={hourSpan}
              >
                {leadingColumn.header}
              </th>
            )}
            <th
              className={[
                styles.HourlyDataTable_BidTableTh,
                styles.HourlyDataTable_BidTableHourBoundaryHeader,
                styles.HourlyDataTable_BidTableStickyTopPrimary,
                styles.HourlyDataTable_BidTableStickyLeftHeader,
              ].join(' ')}
              rowSpan={hourSpan}
            >
              {t('Hour')}
            </th>
            {columnHeaders}
          </tr>
          {subColumnHeaders && <tr>{subColumnHeaders}</tr>}
        </thead>
        <tbody>
          {rowIndices.map((rowIndex) => {
            const dateLabel = getDateLabel?.(rowIndex)
            const prevDateLabel = rowIndex > 0 ? getDateLabel?.(rowIndex - 1) : dateLabel
            const showDateSeparator = rowIndex > 0 && dateLabel !== prevDateLabel

            return (
              <Fragment key={rowIndex}>
                {showDateSeparator && (
                  <tr className={styles.HourlyDataTable_BidTableDateSeparatorRow}>
                    <td className={styles.HourlyDataTable_BidTableDateSeparatorCell} colSpan={totalColumnCount}>
                      {dateLabel}
                    </td>
                  </tr>
                )}
                <tr
                  className={[styles.HourlyDataTable_BidTableRow, getRowClassName?.(rowIndex) ?? '']
                    .filter(Boolean)
                    .join(' ')}
                >
                  {leadingColumn && (
                    <td
                      className={[
                        styles.HourlyDataTable_BidTableLeadingCell,
                        styles.HourlyDataTable_BidTableHourBoundaryCell,
                      ].join(' ')}
                    >
                      {leadingColumn.renderCell(rowIndex)}
                    </td>
                  )}
                  <td
                    className={[
                      styles.HourlyDataTable_BidTableLabelCell,
                      styles.HourlyDataTable_BidTableHourBoundaryCell,
                      styles.HourlyDataTable_BidTableStickyLeftCell,
                    ].join(' ')}
                  >
                    {getRowLabel(rowIndex)}
                  </td>
                  {renderCellsForRow(rowIndex)}
                </tr>
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
